import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { FunctionComponent, memo, Reducer, useEffect, useReducer } from 'react';
import {
  Brush,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useParams } from 'react-router-dom';
import ActionsMenu from '../../components/ActionsMenu/ActionsMenu';
import { useAuthUser } from '../../contexts/AuthProvider/hooks';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import DeviceHealthIcons from './components/DeviceHealthIcons/DeviceHealthIcons';
import Layout from '../../Layout/Layout';
import { dateUTCString } from '../../utils/time/dateString';
import durationString from '../../utils/time/durationString';
import { useAppState } from '../../common';
import WaitingIcon from '@material-ui/icons/WatchLater';
import {
  dialogStateReducer,
  getActionItemsState,
} from '../../components/ActionsMenu/config';
import { decorateDeviceWithStatuses } from '../Devices/config';
import {
  getLabelForDeviceType,
  CHART_COLORS,
  getWeightUnitName,
  getTargetSystemFromID,
  hasBatteryVoltageData,
  hasSignalStrengthData,
  hasActiveToken,
  getSignalTechnologies,
  getSortedDeviceTypes,
  getLastMeasurementAtForDevice,
} from './provider';
import { ChartDataPoint, DeviceModels, RowProps } from './types';
import {
  isNullOrUndefined,
  nullifyIfEmptyOrWhitespace,
  isTotalMeasurementCount,
} from '../../utils/helpers';
import { Chip, Link as MuiLink } from '@material-ui/core';
import { Link } from 'react-router-dom';

import SensorsTable from './components/SensorsTable/SensorsTable';
import { allBluetoothDevices, allGatewayDevices } from './constants';
import {
  useGetDeviceQuery,
  useGetMetricsQuery,
  useGetTargetSystemsQuery,
} from '../../store/api-slice';
import { ListDecoratedMedisanteDevice } from '../Devices/types';
import ActionMenuDialogs from '../../components/ActionsMenu/ActionMenuDialogs';
import { IDialogState } from '../../components/ActionsMenu/types';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
  },
  section: {
    paddingTop: theme.spacing(2),

    [theme.breakpoints.down('xs')]: {
      paddingTop: 0,
    },
  },
  chartPaper: {
    padding: theme.spacing(2),

    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1),
    },
  },
  mobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      '& tbody': {
        display: 'block',
      },
      '& tr': {
        display: 'block',
      },
      '& .MuiTableCell-head': {
        display: 'block',
        padding: theme.spacing(1, 1, 0, 1),
        borderBottom: 'none',
      },
      '& .MuiTableCell-body': {
        display: 'block',
        padding: theme.spacing(0, 1, 1, 1),
        '&:after': {
          content: 'Empty cell',
          visibility: 'hidden',
        },
      },
    },
  },
  page: {
    display: 'flex',
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: {
      '& main': {
        padding: theme.spacing(1, 1, 11, 1),
        paddingTop:
          'calc(' +
          theme.spacing(1) +
          'px + ' +
          theme.mixins.toolbar.minHeight +
          'px)',
      },
    },
  },
  pageTitle: {
    [theme.breakpoints.down('xs')]: {
      '& .MuiGrid-item': {
        maxWidth: 'none',
        flexBasis: '100%',
      },
    },
  },
  health: {
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      textAlign: 'center',
      marginTop: theme.spacing(0),

      '& svg': {
        margin: theme.spacing(3, 0.2, 0, 0.2),
        width: '1.5em',
        height: '1.5em',
        padding: theme.spacing(0.5),
      },
    },
  },
  detailsTitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.fontSize,
      textAlign: 'center',
      display: 'none',
      marginTop: theme.spacing(1),
    },
  },

  deviceTypeChip: {
    marginRight: '0.5rem',
  },
  actions: {
    [theme.breakpoints.down('xs')]: {
      order: -1,
      position: 'fixed',
      top: theme.mixins.toolbar.minHeight,
      left: 0,
      display: 'flex',
      justifyContent: 'flex-end',
      minWidth: '100%',
      backgroundColor: theme.palette.primary.main,
      minHeight: theme.mixins.toolbar.minHeight,
      alignItems: 'center',
      '& .MuiButton-label': {
        color: 'white',
      },
    },
    zIndex: 1,
  },
  updatingState: {
    backgroundColor: theme.palette.secondary.light,
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    top: 2,
    borderRadius: theme.shape.borderRadius,
    padding: '2px 8px 2px 4px',
    color: 'white',
    '& svg': {
      marginRight: 2,
    },
  },

  rowLabel: {
    color: theme.palette.text.secondary,
  },
}));

const DetailsRow: FunctionComponent<RowProps> = ({
  label,
  value,
  shouldHide,
  children,
}) => {
  const classes = useStyles();
  return (!isNullOrUndefined(children) ||
    !isNullOrUndefined(nullifyIfEmptyOrWhitespace(value))) &&
    !shouldHide ? (
    <TableRow>
      <TableCell variant="head" className={classes.rowLabel}>
        {label}
      </TableCell>
      <TableCell> {!isNullOrUndefined(children) ? children : value}</TableCell>
    </TableRow>
  ) : null;
};
const Device = memo(() => {
  const classes = useStyles();
  const { deviceID } = useParams<{ deviceID: string }>();
  const id = decodeURIComponent(deviceID);
  const [actionMenuDialogState, actionMenuUpdateDialogState] = useReducer<
    Reducer<IDialogState, string>
  >(dialogStateReducer, { isMenuOpen: false });
  const config = useConfig();
  const { authUser, selectOrgs } = useAuthUser();
  const measurementRealView = isTotalMeasurementCount();
  const {
    data: fetchedDevice,
    isFetching: isLoading,
    error,
  } = useGetDeviceQuery({ apiBaseURL: config.apiBaseURL, id });
  let gateway,
    isGatewayLoading,
    gatewayError = null;
  const {
    data: gatewayData,
    isFetching,
    error: gateError,
  } = useGetDeviceQuery({
    apiBaseURL: config.apiBaseURL,
    id: fetchedDevice?.gatewayID || null,
  });
  if (fetchedDevice?.gatewayID) {
    gateway = gatewayData;
    isGatewayLoading = isFetching;
    gatewayError = gateError;
  }
  const { data: metrics = [] } = useGetMetricsQuery({
    apiBaseURL: config.apiBaseURL,
    deviceID: id,
  });

  const { data: targetSystems = [] } = useGetTargetSystemsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: authUser.selectedOrgs,
    filters: [],
  });

  const device = fetchedDevice
    ? decorateDeviceWithStatuses(fetchedDevice, authUser)
    : null;

  const actionItemState = device ? getActionItemsState(authUser, [device]) : {};

  useEffect(() => {
    if (device && authUser && authUser.selectedOrgs.length > 0) {
      if (!authUser.selectedOrgs.includes(device.owner)) {
        selectOrgs([device.owner]);
      }
    }
  }, [device, authUser, selectOrgs]);

  const hasAvailableTargetSystems = targetSystems.length > 0;
  const signalTechnologies = getSignalTechnologies(metrics);

  const getChartData = () =>
    metrics.map((m) => {
      let d: ChartDataPoint = {
        timestamp: new Date(m.timestamp).getTime(),
      };
      if (m.data) {
        if (m.data.batteryVoltage) {
          d = { ...d, batteryVoltage: parseFloat(m.data.batteryVoltage) };
          if (device) {
            if (device.versionInfo.batteryVoltageMin) {
              d = {
                ...d,
                batteryVoltageMin: device.versionInfo.batteryVoltageMin,
              };
            }
            if (device.versionInfo.batteryVoltageLow) {
              d = {
                ...d,
                batteryVoltageLow: device.versionInfo.batteryVoltageLow,
              };
            }
            if (device.versionInfo.signalStrengthMin) {
              d = {
                ...d,
                signalStrengthMin: device.versionInfo.signalStrengthMin,
              };
            }
            if (device.versionInfo.signalStrengthLow) {
              d = {
                ...d,
                signalStrengthLow: device.versionInfo.signalStrengthLow,
              };
            }
          }
        }
        if (m.data.signalTechnology && m.data.signalStrength) {
          d = {
            ...d,
            [`signalStrength${m.data.signalTechnology}`]: parseFloat(
              m.data.signalStrength
            ),
          };
        }
      }
      return d;
    });

  const data = getChartData();

  const containsProperty = (device: ListDecoratedMedisanteDevice) => {
    if (!device.properties) {
      return false;
    }
    if (device.properties.testdevice) {
      return true;
    }
    if (!device.properties.status) {
      return false;
    }
    return device.properties.status.length > 0;
  };

  const isMuted = (device: ListDecoratedMedisanteDevice) => {
    return (
      device.properties &&
      device.properties.muted &&
      device.properties.muted.length > 0
    );
  };

  let deviceState: any = '';
  const { updateDeviceQueue } = useAppState();

  if (device) {
    deviceState = device.state;

    if (updateDeviceQueue) {
      const ts = updateDeviceQueue[device.id];
      if (ts) {
        deviceState = (
          <span
            className={[
              classes.updatingState,
              (ts + '-device-state').toLowerCase(),
            ].join(' ')}
          >
            <WaitingIcon /> {ts}
          </span>
        );
      }
    }
  }

  const sortedDeviceTypes = getSortedDeviceTypes(device);

  const isGateway =
    !!device && allGatewayDevices.includes(device.model as DeviceModels);
  const deviceIsSensor =
    !!device && allBluetoothDevices.includes(device.model as DeviceModels);

  const hasHWInfo = Boolean(device?.batteryVoltage && device.signalStrength);

  const isUnlinked = device?.allowUnlinkedGateway;

  return (
    <div className={classes.page}>
      <Layout
        isLoading={isLoading || isGatewayLoading}
        error={error || gatewayError}
      >
        {device !== null && (
          <>
            <section className={classes.section}>
              <Grid
                className={classes.pageTitle}
                container
                justifyContent="space-between"
              >
                <Grid item xs={6}>
                  <Typography variant="h4" className={classes.title}>
                    <span className={classes.detailsTitle}>Device details</span>
                  </Typography>
                </Grid>
                <Grid
                  className={classes.actions}
                  item
                  xs={6}
                  style={{ textAlign: 'right' }}
                >
                  <ActionsMenu
                    devices={[device]}
                    dialogState={actionMenuDialogState}
                    updateDialogState={actionMenuUpdateDialogState}
                  />
                </Grid>
              </Grid>
            </section>

            <section className={classes.section}>
              <Table className={classes.mobile}>
                <TableBody>
                  <DetailsRow
                    label="Serial number"
                    value={device.serialNumber}
                  />
                  <DetailsRow
                    label="Model"
                    value={`${
                      device.properties && device.properties.model
                        ? device.properties.model.join(' ')
                        : device.model
                    } (${device.manufacturer})`}
                  />
                  <DetailsRow label="Version" value={device.version} />
                  <DetailsRow
                    label="Device type"
                    shouldHide={sortedDeviceTypes.length === 0}
                  >
                    {getSortedDeviceTypes(device).map((t) => (
                      <Chip
                        className={classes.deviceTypeChip}
                        key={t}
                        label={getLabelForDeviceType(t)}
                      />
                    ))}
                  </DetailsRow>
                  <DetailsRow
                    label="Region"
                    shouldHide={
                      !device.properties ||
                      (device.properties && !device.properties.region)
                    }
                  >
                    {device.properties &&
                      device.properties.region &&
                      device.properties.region.map((r) => (
                        <Chip
                          className={classes.deviceTypeChip}
                          key={r}
                          label={r}
                        />
                      ))}
                  </DetailsRow>
                  <DetailsRow
                    label="Status"
                    shouldHide={
                      deviceIsSensor ||
                      !(
                        (device.properties &&
                          device.properties.status &&
                          device.properties.status.length > 0) ||
                        isMuted(device) ||
                        (device.statuses &&
                          Object.values(device.statuses).some((s) => s)) ||
                        hasHWInfo ||
                        device.lastStatusCode > 0 ||
                        (device.properties &&
                          device.properties.testdevice &&
                          device.properties.testdevice.length > 0)
                      )
                    }
                  >
                    <DeviceHealthIcons device={device} />
                  </DetailsRow>
                  <DetailsRow
                    label="Status"
                    shouldHide={!deviceIsSensor || !containsProperty(device)}
                  >
                    <DeviceHealthIcons device={device} />
                  </DetailsRow>
                  <DetailsRow label="IMEI" value={device.imei} />
                  <DetailsRow
                    label="ICCID"
                    value={device.iccid ? device.iccid : '-'}
                  />
                  <DetailsRow
                    label="MAC"
                    value={device.mac}
                    shouldHide={!device.mac}
                  />
                  <DetailsRow label="Organisation ID" value={device.owner} />
                  <DetailsRow
                    label="Manufactured (UTC)"
                    value={dateUTCString(new Date(device.manufacturedAt))}
                  />
                  <DetailsRow
                    shouldHide={!config.isDeviceStateFeatureEnabled}
                    label="State"
                    value={deviceState}
                  />
                  <DetailsRow
                    shouldHide={!Boolean(device.timeZone) || deviceIsSensor}
                    label="Time zone"
                    value={device.timeZone}
                  />
                  <DetailsRow
                    shouldHide={!Boolean(device.weightUnit)}
                    label="Weight unit"
                    value={getWeightUnitName(device.weightUnit!)}
                  />
                  <DetailsRow
                    shouldHide={!Boolean(device.bloodGlucoseUnit)}
                    label="Blood glucose unit"
                    value={device.bloodGlucoseUnit}
                  />
                  <DetailsRow
                    label="Sync config"
                    shouldHide={
                      !device.heartbeatTimerDuration ||
                      device.heartbeatTimerDuration <= 0
                    }
                  >
                    Every {durationString(device.heartbeatTimerDuration!)}
                    {device.heartbeatRetryCount !== undefined &&
                      device.heartbeatRetryCount > 0 &&
                      device.heartbeatRetryCountTime !== undefined &&
                      ` (retrying ${device.heartbeatRetryCount} time${
                        device.heartbeatRetryCount > 1 ? 's' : ''
                      } every ${durationString(
                        device.heartbeatRetryCountTime
                      )})`}
                  </DetailsRow>

                  <DetailsRow
                    label="Number of measurements"
                    shouldHide={isGateway}
                  >
                    {measurementRealView
                      ? device.measurementCount
                      : device.measurementTripCount}
                  </DetailsRow>

                  <DetailsRow label="Last measurement (UTC)">
                    {dateUTCString(
                      getLastMeasurementAtForDevice(
                        device,
                        authUser.orgs.filter(
                          (org) =>
                            authUser.selectedOrgs.includes(org.id) &&
                            org.id === device?.owner &&
                            org.demo
                        ).length > 0
                      )
                    )}
                  </DetailsRow>
                  <DetailsRow
                    label="Last communication (UTC)"
                    value={dateUTCString(
                      device.lastCommunicationAt
                        ? new Date(device.lastCommunicationAt)
                        : null
                    )}
                  />
                  <DetailsRow
                    shouldHide={!hasActiveToken(device)}
                    label="Token issued (UTC)"
                    value={dateUTCString(
                      device.token
                        ? device.token.confirmed
                          ? new Date(device.token.confirmed.issued)
                          : null
                        : null
                    )}
                  />
                  <DetailsRow
                    shouldHide={
                      !hasAvailableTargetSystems ||
                      device.targetSystems.length === 0 ||
                      (deviceIsSensor && !isUnlinked) ||
                      (isGateway && isUnlinked)
                    }
                    label="Webhooks"
                  >
                    {device.targetSystems.map((id, index) => {
                      const ts = getTargetSystemFromID(id, targetSystems);
                      return (
                        <Tooltip key={ts.id} title={ts.url}>
                          <span>
                            {ts.name}
                            {index < device.targetSystems.length - 1
                              ? ', '
                              : ''}
                          </span>
                        </Tooltip>
                      );
                    })}
                  </DetailsRow>
                  <DetailsRow
                    shouldHide={!config.isTagsFeatureEnabled}
                    label="Tags"
                    value={device.tags.join(', ')}
                  />
                  <DetailsRow
                    shouldHide={!actionItemState.showChangeMinimumWeightFilter}
                    label="Minimum weight filter"
                    value={
                      device.minimumWeight ? device.minimumWeight + 'kg' : ''
                    }
                  />

                  <DetailsRow
                    shouldHide={!deviceIsSensor || isUnlinked}
                    label="Gateway"
                  >
                    {gateway ? (
                      <MuiLink
                        color="textPrimary"
                        href="#"
                        component={Link}
                        to={`/devices/${encodeURIComponent(gateway.id)}`}
                      >
                        {`${gateway.model} - ${gateway.serialNumber}`}
                      </MuiLink>
                    ) : (
                      '-'
                    )}
                  </DetailsRow>
                </TableBody>
              </Table>
            </section>
            <br />

            {metrics.length > 0 && (
              <>
                {hasBatteryVoltageData(data) && (
                  <section className={classes.section}>
                    <Paper className={classes.chartPaper}>
                      <Grid container justifyContent="space-between">
                        <Grid item xs={6}>
                          <Typography variant="subtitle1">
                            Battery voltage
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ textAlign: 'right' }}>
                          {device.batteryVoltage && (
                            <Typography variant="subtitle1">
                              {device.batteryVoltage} V
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                      <br />
                      <ResponsiveContainer height={300}>
                        <LineChart data={data} syncId="synced">
                          <XAxis
                            dataKey="timestamp"
                            type="number"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(t) =>
                              dateUTCString(new Date(Number(t)))
                            }
                          />
                          <Line
                            name="Battery Voltage"
                            dataKey="batteryVoltage"
                            type="monotone"
                            stroke={CHART_COLORS[0]}
                            strokeWidth={1.5}
                            dot={{
                              stroke: CHART_COLORS[0],
                              fill: CHART_COLORS[0],
                              r: 2,
                            }}
                          />
                          {device.versionInfo.batteryVoltageMin &&
                            device.versionInfo.batteryVoltageLow && (
                              <ReferenceArea
                                y1={device.versionInfo.batteryVoltageMin}
                                y2={device.versionInfo.batteryVoltageLow}
                                fill="#ffff7f"
                              />
                            )}
                          {device.versionInfo.batteryVoltageMin && (
                            <ReferenceArea
                              y2={device.versionInfo.batteryVoltageMin}
                              fill="rgb(255, 150, 0)"
                            />
                          )}
                          <YAxis
                            unit=" V"
                            domain={[
                              (dataMin: any) =>
                                Math.min(
                                  device.versionInfo.batteryVoltageMin || 0,
                                  dataMin
                                ),
                              'auto',
                            ]}
                          />
                          <ChartTooltip
                            labelFormatter={(t) =>
                              dateUTCString(new Date(Number(t)))
                            }
                            formatter={(v: any) => `${v} V`}
                          />
                          <Brush
                            dataKey="timestamp"
                            tickFormatter={(t) =>
                              dateUTCString(new Date(Number(t)))
                            }
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </section>
                )}

                {hasSignalStrengthData(data) && (
                  <section className={classes.section}>
                    <Paper className={classes.chartPaper}>
                      <Grid container justifyContent="space-between">
                        <Grid item xs={6}>
                          <Typography variant="subtitle1">
                            Signal strength
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ textAlign: 'right' }}>
                          {device.signalStrength && (
                            <Typography variant="subtitle1">
                              {device.signalStrength} dBm
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                      <br />
                      <ResponsiveContainer height={300}>
                        <LineChart
                          margin={{ top: 20 }}
                          data={data}
                          syncId="synced"
                        >
                          <XAxis
                            dataKey="timestamp"
                            type="number"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(t) =>
                              dateUTCString(new Date(Number(t)))
                            }
                          />
                          <YAxis
                            unit=" dBm"
                            domain={[
                              (dataMin: any) => Math.min(-121, dataMin),
                              (dataMax: any) => Math.max(-60, dataMax),
                            ]}
                          />
                          {device.versionInfo.signalStrengthMin &&
                            device.versionInfo.signalStrengthLow && (
                              <ReferenceArea
                                y1={device.versionInfo.signalStrengthMin}
                                y2={device.versionInfo.signalStrengthLow}
                                fill="#ffff7f"
                              />
                            )}
                          {device.versionInfo.signalStrengthMin && (
                            <ReferenceArea
                              y1={-121}
                              y2={device.versionInfo.signalStrengthMin}
                              fill="rgb(255, 150, 0)"
                            />
                          )}
                          {signalTechnologies.map((t, i) => (
                            <Line
                              key={i}
                              name={`${t} Signal Strength`}
                              dataKey={`signalStrength${t}`}
                              type="monotone"
                              stroke={CHART_COLORS[i]}
                              strokeWidth={1.5}
                              dot={{
                                stroke: CHART_COLORS[i],
                                fill: CHART_COLORS[i],
                                r: 2,
                              }}
                            />
                          ))}
                          <ChartTooltip
                            labelFormatter={(t) =>
                              dateUTCString(new Date(Number(t)))
                            }
                            formatter={(v: any) => `${v} dBm`}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </section>
                )}
              </>
            )}
            {isGateway &&
            device.sensors &&
            device.sensors.length > 0 &&
            !isUnlinked ? (
              <SensorsTable
                totalView={measurementRealView}
                sensors={device.sensors || []}
              />
            ) : null}
          </>
        )}
      </Layout>
      <ActionMenuDialogs
        dialogState={actionMenuDialogState}
        updateDialogState={actionMenuUpdateDialogState}
        actualDevices={fetchedDevice ? [fetchedDevice] : []}
      />
    </div>
  );
});

export default Device;
